import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import JsonLd from '../components/JsonLd';
import { getSiteOrigin } from '../lib/siteMeta';
import { blogPosts as mockBlog } from '../mock/mockData';
import { fetchBlog } from '../lib/api';
import { Calendar, Clock, ArrowRight, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const BlogPage = () => {
  const [blogPosts, setBlogPosts] = useState(mockBlog);
  useEffect(() => {
    fetchBlog().then((data) => data?.length && setBlogPosts(data)).catch(() => {});
  }, []);
  const blogListSchema = useMemo(() => {
    const origin = getSiteOrigin();
    if (!blogPosts.length) {
      return { '@context': 'https://schema.org', '@type': 'ItemList', itemListElement: [] };
    }
    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: blogPosts.map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `${origin}/actualites#article-${p.id}`,
        name: p.title,
      })),
    };
  }, [blogPosts]);

  const [featured, ...rest] = blogPosts;
  if (!featured) {
    return (
      <div className="min-h-screen bg-white">
        <Seo
          title="Actualités & blog — DPC, financements, pratique médicale"
          description="Articles Medelia sur la formation continue, le DPC, les financements (FIFPL, OPCO, CPF) et les bonnes pratiques pour les professionnels de santé en France."
          canonicalPath="/actualites"
        />
        <JsonLd data={blogListSchema} />
        <Navbar />
        <main className="safe-pt-nav px-5 lg:px-8 py-20 text-center text-slate-600">Aucun article pour le moment.</main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Actualités & blog — DPC, financements, pratique médicale"
        description="Articles Medelia sur la formation continue, le DPC, les financements (FIFPL, OPCO, CPF) et les bonnes pratiques pour les professionnels de santé en France."
        canonicalPath="/actualites"
      />
      <JsonLd data={blogListSchema} />
      <Navbar />
      <main className="safe-pt-nav">
        <section className="py-16 bg-gradient-to-br from-teal-50/60 via-white to-emerald-50/40">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <span className="text-teal-600 text-sm font-bold uppercase tracking-widest">Actualités</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-3 tracking-tight max-w-2xl">
              Le blog Medelia : infos & conseils pour les pros de santé
            </h1>
            <p className="text-slate-600 mt-4 max-w-xl">Toute l'actualité du DPC, des financements et des meilleures pratiques médicales.</p>
          </div>
        </section>

        <section className="py-14">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-6 mb-12">
              <div
                id={featured.id != null ? `article-${featured.id}` : undefined}
                className="group relative rounded-3xl overflow-hidden aspect-[16/10] cursor-pointer"
              >
                <img src={featured.img} alt={featured.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-7 text-white">
                  <Badge className="bg-teal-500 hover:bg-teal-500 text-white border-0 mb-3">{featured.category}</Badge>
                  <h2 className="text-2xl lg:text-3xl font-bold leading-tight mb-3">{featured.title}</h2>
                  <p className="text-slate-200 text-sm mb-4 line-clamp-2">{featured.excerpt}</p>
                  <div className="flex items-center gap-4 text-xs text-slate-300">
                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {featured.date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {featured.readTime}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-5">
                {rest.map((p) => (
                  <div
                    key={p.id}
                    id={`article-${p.id}`}
                    className="group grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-4 sm:gap-5 bg-white border border-slate-200 rounded-2xl p-4 hover:border-teal-300 hover:shadow-md transition-all cursor-pointer"
                  >
                    <img src={p.img} alt={p.title} className="w-full h-full object-cover rounded-xl aspect-square" />
                    <div className="py-2">
                      <Badge variant="outline" className="text-[10px] mb-2 border-teal-200 text-teal-700 bg-teal-50">{p.category}</Badge>
                      <h3 className="font-bold text-slate-900 leading-snug group-hover:text-teal-700 transition-colors line-clamp-2 mb-2">{p.title}</h3>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {p.date}</span>
                        <span>{p.readTime}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-50/60 border border-slate-200 rounded-3xl p-10 text-center">
              <Tag className="w-10 h-10 text-teal-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Ne manquez aucune actualité</h3>
              <p className="text-slate-600 mb-5 max-w-md mx-auto">Recevez nos derniers articles et les nouveautés du secteur directement par email.</p>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white">S'abonner à la newsletter <ArrowRight className="w-4 h-4 ml-1" /></Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPage;
